import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  PLATFORM_ID,
  Provider,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';

import { isPlatformBrowser } from '@angular/common';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { Analytics, UserTrackingService, getAnalytics } from '@angular/fire/analytics';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  provideFirestore,
} from '@angular/fire/firestore';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@angular/fire/functions';
import { Messaging, getMessaging } from '@angular/fire/messaging';
import { Performance, getPerformance } from '@angular/fire/performance';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import {
  provideClientHydration,
  withEventReplay,
  withNoIncrementalHydration,
} from '@angular/platform-browser';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { initializeAuth } from './core/initializers/auth.initializer';
import { GlobalErrorHandler } from './core/services/error-handler.service';
import { initializeFacebookPixel } from './core/initializers/facebook-pixel.initializer';
import { initializeGtm } from './core/initializers/gtm.initializer';
import { initializeMarketingAttribution } from './core/initializers/marketing-attribution.initializer';
import { APP_STATES } from './core/states';

// Factory to conditionally provide Analytics (browser only)
function provideAnalyticsBrowser(): Provider[] {
  return [
    {
      provide: Analytics,
      useFactory: (platformId: object) => {
        if (isPlatformBrowser(platformId)) {
          try {
            return getAnalytics();
          } catch {
            return null;
          }
        }
        return null;
      },
      deps: [PLATFORM_ID],
    },
  ];
}

// Factory to conditionally provide Messaging (browser only)
function provideMessagingBrowser(): Provider[] {
  return [
    {
      provide: Messaging,
      useFactory: (platformId: object) => {
        if (isPlatformBrowser(platformId)) {
          try {
            return getMessaging();
          } catch {
            return null;
          }
        }
        return null;
      },
      deps: [PLATFORM_ID],
    },
  ];
}

// Factory to conditionally provide Performance (browser only)
function providePerformanceBrowser(): Provider[] {
  return [
    {
      provide: Performance,
      useFactory: (platformId: object) => {
        if (isPlatformBrowser(platformId)) {
          try {
            return getPerformance();
          } catch {
            return null;
          }
        }
        return null;
      },
      deps: [PLATFORM_ID],
    },
  ];
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideHttpClient(withFetch()),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
    ),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = getAuth();
      if (environment.useEmulators && environment.emulators) {
        connectAuthEmulator(
          auth,
          `http://${environment.emulators.auth.host}:${environment.emulators.auth.port}`,
          { disableWarnings: true },
        );
      }
      return auth;
    }),
    provideDatabase(() => getDatabase()),
    ...provideAnalyticsBrowser(),
    UserTrackingService,
    provideFirestore(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const databaseId = (environment.firebase as any).databaseId;
      // Auto-detect long polling falls back from WebChannel under throttled
      // networks (mobile, Lighthouse PSI), avoiding Listen-channel ERR_TIMED_OUT
      // errors that drop Best Practices score.
      const settings = { experimentalAutoDetectLongPolling: true };
      let firestore: ReturnType<typeof getFirestore>;
      try {
        firestore =
          databaseId && databaseId !== '(default)'
            ? initializeFirestore(getApp(), settings, databaseId)
            : initializeFirestore(getApp(), settings);
      } catch (err) {
        // Only swallow the known "already initialized" case (HMR, double-provide
        // during route changes, etc.) — Firestore throws code "failed-precondition"
        // with a message containing "already" when initializeFirestore is called
        // twice. Anything else is a real misconfiguration and should surface.
        const code = (err as { code?: string })?.code;
        const message = (err as { message?: string })?.message ?? '';
        if (code === 'failed-precondition' || /already/i.test(message)) {
          firestore =
            databaseId && databaseId !== '(default)'
              ? getFirestore(getApp(), databaseId)
              : getFirestore();
        } else {
          throw err;
        }
      }
      if (environment.useEmulators && environment.emulators) {
        connectFirestoreEmulator(
          firestore,
          environment.emulators.firestore.host,
          environment.emulators.firestore.port,
        );
      }
      return firestore;
    }),
    provideFunctions(() => {
      const functions = getFunctions(getApp(), environment.functionsRegion);
      if (environment.useEmulators && environment.emulators) {
        connectFunctionsEmulator(
          functions,
          environment.emulators.functions.host,
          environment.emulators.functions.port,
        );
      }
      return functions;
    }),
    ...provideMessagingBrowser(),
    ...providePerformanceBrowser(),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulators && environment.emulators) {
        connectStorageEmulator(
          storage,
          environment.emulators.storage.host,
          environment.emulators.storage.port,
        );
      }
      return storage;
    }),
    provideStore(
      APP_STATES,
      // withNgxsFormPlugin(),
      ...(isDevMode()
        ? [
            withNgxsReduxDevtoolsPlugin(),
            // withNgxsLoggerPlugin()
          ]
        : []),
    ),
    // Initialize auth state BEFORE app starts routing
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      multi: true,
    },
    // Initialize Google Tag Manager
    {
      provide: APP_INITIALIZER,
      useFactory: initializeGtm,
      multi: true,
    },
    // Initialize Facebook (Meta) Pixel
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFacebookPixel,
      multi: true,
    },
    // Capture Google Ads click ids (gclid/wbraid/gbraid) from the landing URL
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMarketingAttribution,
      multi: true,
    },
    provideClientHydration(withEventReplay(), withNoIncrementalHydration()),
  ],
};
